/**
 * V5 详细报告 mock —— Path C：前端按"假设 schema"驱动 UI，后端跟进时只换数据源。
 *
 * 假设的 schema 扩展（提议详见 docs/archive/v5/AI-HANDOFF.md 2026-05-20 14:00 frontend 条目）：
 * 在现有 user_report_v5 内追加一个 `detailed` 块，承载详细报告的额外字段。
 * 粗略字段（overall / abilities / LLM 字段）原样复用，不重复。
 *
 *   user_report_v5.detailed = {
 *     ability_deep_dive:  { <ability_code>: {current_state_summary, impact_and_direction} } × 5
 *     growth_path: {
 *       overall_goal: string (50-100),
 *       phases: [{phase_index, phase_label, phase_title, phase_goal,
 *                  learning_items[4-6], focus_ability_codes[1-3]}] × 3,
 *       follow_up_suggestions: string[] (3-5 items, 15-30 chars each),
 *       application_tips: string (80-120)
 *     }
 *   }
 *
 * 素质（trait_deep_dive / trait_synthesis）已永久从 detailed 块移除（Task 11）。
 * ⚠️ 这只是前端的"提议"。后端确认（或修订）schema 之后，按真版本回填，adapter 不动。
 */
import { mockReportRawPayload } from './v5ReportMock';

// ============================================================================
// detailed 块（按 L2 惯用期人设合成，与 mockReportRawPayload 同一个用户视角）
// ============================================================================
const abilityDeepDive = {
  ai_leverage: {
    current_state_summary:
      '你已能熟练借助 AI 工具完成日常工作任务，是当前的相对优势。能识别常见场景下 AI 的可用性，并有效利用 AI 提升个人效率。',
    impact_and_direction:
      '影响：能在多数任务中比同伴更快得到可用的产出。提升方向：把高频任务的 AI 用法模板化，逐步形成自己的"借力工具箱"，让借力从临时动作变成固定流程。',
  },
  ai_symbiosis: {
    current_state_summary:
      '你能与 AI 进行较顺畅的多轮协同，在指令表达和迭代上处于中上水平。',
    impact_and_direction:
      '影响：能让 AI 在多数任务上一次给出可用初稿。提升方向：把高频任务的提示词模板化、记进自己的提示词本，下次同类任务直接套用，进一步压缩协同成本。',
  },
  ai_team_leadership: {
    current_state_summary:
      '你的 AI 使用主要停留在个人层面，对团队成员的 AI 使用带动和引导较少，尚未形成系统性的团队 AI 赋能习惯。',
    impact_and_direction:
      '影响：团队整体 AI 协同效率偏低，个人能力难以复制到团队。提升方向：每周分享一个自己跑通的 AI 用法，帮助团队成员找到适合自己岗位的切入点。',
  },
  ai_governance: {
    current_state_summary:
      '在涉及客户资料、敏感信息、对外正式内容时，对 AI 使用边界与风险的把控机制尚不完善。',
    impact_and_direction:
      '影响：数据安全与合规风险敞口偏高。提升方向：为团队梳理一份 AI 使用规范，明确可用场景、禁止传入的数据类型、必须人工复核的输出类型。',
  },
  ai_strategic_foresight: {
    current_state_summary:
      '你对 AI 行业趋势有一定关注，但尚未形成系统性的战略预判和组织布局能力。',
    impact_and_direction:
      '影响：容易跟随行业节奏而非主动布局。提升方向：每月关注 1-2 个行业 AI 实践案例，思考与本团队业务的结合点，逐步从"了解趋势"迈向"主动布局"。',
  },
};

// collaboration_style / not_recommended_tasks 已随 Page 4 三列表改版停用并删除。

const growthPath = {
  overall_goal:
    '用 30 天把当前的"惯用期"水平迁移到"扩散期"，重点在带队赋能 + AI 治理规范 + 战略预判三件事上跑通固定动作。',
  phases: [
    {
      phase_index: 1,
      phase_label: '第 1-10 天',
      phase_title: '建立个人 AI 借力模板库',
      phase_goal: '把现有高频 AI 用法模板化，形成可复用的个人"借力工具箱"',
      learning_items: [
        '梳理 5-8 个本职工作中的常见任务，逐个标注是否适合 AI',
        '为每类适合 AI 的任务，写 1 个稳定可复用的提示词模板',
        '每周记录 2-3 次 AI 出错的情境，沉淀到自己的边界清单',
        '读 1 篇 prompt engineering 进阶资料 + 看 1 个同行的高质量提示词案例',
      ],
      focus_ability_codes: ['ai_leverage', 'ai_symbiosis'],
    },
    {
      phase_index: 2,
      phase_label: '第 11-20 天',
      phase_title: '带动团队 + 建立基础治理规范',
      phase_goal: '开始带动团队成员使用 AI，并建立基础的 AI 使用边界规范',
      learning_items: [
        '每周挑选 1 个自己跑通的 AI 用法，以 10 分钟分享形式传递给团队',
        '观察团队成员在使用 AI 时的常见困难，给出针对性的建议',
        '梳理团队 AI 使用规范草稿：可用场景 / 禁止传入数据 / 必须复核输出',
        '收集团队对 AI 使用规范的反馈，更新一轮后公示',
      ],
      focus_ability_codes: ['ai_team_leadership', 'ai_governance'],
    },
    {
      phase_index: 3,
      phase_label: '第 21-30 天',
      phase_title: '提升战略预判，从使用走向布局',
      phase_goal: '开始关注 AI 行业趋势并与业务结合，形成初步的战略预判视角',
      learning_items: [
        '精读 1-2 个同行业 AI 最佳实践案例，思考与本团队业务的结合点',
        '梳理未来 3 个月内可能被 AI 影响的 2-3 个核心工作流，提出改进建议',
        '与直线上级或同级讨论一次团队 AI 能力建设方向',
        '为下一季度设定一个团队 AI 能力目标（如：3 人熟练使用 AI 辅助某类任务）',
      ],
      focus_ability_codes: ['ai_strategic_foresight'],
    },
  ],
  follow_up_suggestions: [
    '每月底做一次"AI 协同复盘"，看哪些任务用得好、哪些还在试错',
    '建立团队共享的提示词库，按任务类型分类，目标半年内积累 30+ 条',
    '关注 1-2 个行业领先公司的 AI 实践，借鉴他们的人机分工模式',
    '若团队尚无 AI 使用规范，主动牵头梳理一版小型的合规边界清单',
  ],
  application_tips:
    '不必追求一次性跑通所有动作。挑你本职工作里频次最高的 2-3 个任务作为起点，把这 30 天的方法先在它们上跑稳，再向其它任务扩展。',
};

// ============================================================================
// 完整 detailed payload：以 mockReportRawPayload 为基底 + detailed 块
// ============================================================================
const baseInner = mockReportRawPayload.user_report_v5;

export const mockReportDetailedRawPayload = {
  status: 'done',
  report_version: 5,
  is_fallback: false,
  is_detailed_unlocked: true,   // 能看到 detailed payload 意味着已解锁（后端只对 unlocked 返回 detailed 内容）
  user_report_v5: {
    ...baseInner,
    detailed: {
      ability_deep_dive: abilityDeepDive,
      growth_path: growthPath,
    },
  },
};

// 子块导出
export const mockDetailedBlock = mockReportDetailedRawPayload.user_report_v5.detailed;
